import { HttpErrorResponse } from '@angular/common/http';

export type ForbiddenKind = 'session-invalid' | 'permission' | 'unknown';

export interface ForbiddenClassification {
  kind: ForbiddenKind;
  source: 'error-code' | 'details-reason' | 'refresh-endpoint' | 'legacy-text' | 'unknown';
  errorCode: string | null;
  reason: string | null;
}

const SESSION_INVALID_STRUCTURED_MARKERS = [
  'SESSION_INVALID',
  'SESSION_REVOKED',
  'SESSION_INVALIDATED',
  'TOKEN_INVALID',
  'TOKEN_REVOKED',
  'REFRESH_TOKEN_REVOKED',
  'USER_INACTIVE',
  'ACCOUNT_DEACTIVATED',
  'USER_DISABLED',
  'AUTH_REVOKED',
] as const;

const PERMISSION_STRUCTURED_MARKERS = [
  'FORBIDDEN',
  'PERMISSION_DENIED',
  'INSUFFICIENT_PERMISSIONS',
  'ACCESS_DENIED',
  'ROLE_INVALID',
  'INVALID_ROLE',
  'ROLE_NOT_ALLOWED',
] as const;

const LEGACY_TEXT_FALLBACK_ENABLED = true;

const SESSION_INVALID_MARKERS = [
  'inactive',
  'deactivated',
  'disabled',
  'revoked',
  'token version',
  'stale token',
  'session invalid',
  'invalid session',
  'invalid token',
  'token invalid',
  'refresh token',
  'refresh session',
];

export function classifyForbidden(
  error: HttpErrorResponse,
  requestUrl: string
): ForbiddenClassification {
  if (requestUrl.includes('/auth/refresh')) {
    return {
      kind: 'session-invalid',
      source: 'refresh-endpoint',
      errorCode: null,
      reason: null,
    };
  }

  const body = error.error;
  const errorCode = normalizeStructuredValue(
    body && typeof body === 'object' ? body['errorCode'] : null
  );
  const reason = normalizeStructuredValue(extractDetailsReason(body));

  if (matchesStructuredMarker(errorCode, SESSION_INVALID_STRUCTURED_MARKERS)) {
    return {
      kind: 'session-invalid',
      source: 'error-code',
      errorCode,
      reason,
    };
  }

  if (matchesStructuredMarker(reason, SESSION_INVALID_STRUCTURED_MARKERS)) {
    return {
      kind: 'session-invalid',
      source: 'details-reason',
      errorCode,
      reason,
    };
  }

  if (matchesStructuredMarker(errorCode, PERMISSION_STRUCTURED_MARKERS)) {
    return {
      kind: 'permission',
      source: 'error-code',
      errorCode,
      reason,
    };
  }

  if (matchesStructuredMarker(reason, PERMISSION_STRUCTURED_MARKERS)) {
    return {
      kind: 'permission',
      source: 'details-reason',
      errorCode,
      reason,
    };
  }

  if (LEGACY_TEXT_FALLBACK_ENABLED) {
    const details = extractErrorText(error);
    if (SESSION_INVALID_MARKERS.some((marker) => details.includes(marker))) {
      return {
        kind: 'session-invalid',
        source: 'legacy-text',
        errorCode,
        reason,
      };
    }
  }

  return {
    kind: 'unknown',
    source: 'unknown',
    errorCode,
    reason,
  };
}

export function isSessionInvalidForbidden(
  error: HttpErrorResponse,
  requestUrl: string
): boolean {
  return classifyForbidden(error, requestUrl).kind === 'session-invalid';
}

function extractDetailsReason(body: unknown): unknown {
  if (!body || typeof body !== 'object') {
    return null;
  }
  const bodyRecord = body as Record<string, unknown>;
  const details = bodyRecord['details'];
  if (!details || typeof details !== 'object') {
    return null;
  }
  return (details as Record<string, unknown>)['reason'];
}

function normalizeStructuredValue(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  return value
    .trim()
    .replace(/[\s\-]+/g, '_')
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .toUpperCase();
}

function matchesStructuredMarker(
  descriptor: string | null,
  markers: readonly string[]
): boolean {
  if (!descriptor) {
    return false;
  }
  return markers.some((marker) => descriptor.includes(marker));
}

function extractErrorText(error: HttpErrorResponse): string {
  const body = error.error;
  if (typeof body === 'string') {
    return body.toLowerCase();
  }
  if (body && typeof body === 'object') {
    return [
      body['message'],
      body['error'],
      JSON.stringify(body['details'] ?? ''),
      JSON.stringify(body['validationErrors'] ?? ''),
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
  }
  return (error.message || '').toLowerCase();
}
